import { useEffect, useState } from 'react';
import type { Me } from '../api/auth';
import type { NotificationCategoryRow, NotificationSettings } from '../api/notifications';
import { notificationsApi } from '../api/notifications';
import { AppHeader } from '../ui/AppHeader';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { ToggleRow } from '../ui/ToggleRow';

/**
 * What reaches you, and what doesn't — TROOP-192.
 *
 * **On `SettingsScreen`'s side of the split, not `ManageScreen`'s.** These follow the person rather
 * than the unit: a parent with a Scout in Troop 119 and a Cub in Pack 119 has one set of them, the
 * same argument that puts their calendar feed and their car here. It is also why a guardian holding
 * no membership anywhere can reach it — the API is under `/me/`, which `UnitScopeMiddleware` never
 * sees.
 *
 * **The copy on each row is the only thing this screen decides.** Which categories exist, whether
 * each may be switched off, and why not, all arrive from the server: §20 is the source of truth and
 * a second copy here is one that drifts the first time a row is added to that table. In particular
 * `canMute` is never assumed — Safety & account renders `whyNot` as `ToggleRow`'s `disabledReason`,
 * and the server refuses the write regardless of what this screen draws.
 */
const COPY: Record<NotificationCategoryRow['category'], { label: string; secondary: string }> = {
  Announcements: {
    label: 'Announcements',
    secondary: "What the unit posts for everyone. The one channel that exists to be read.",
  },
  EventChanges: {
    label: 'Event changes',
    secondary:
      "A trip cancelled, or a time or place moving — never every small edit, which would teach you to ignore these. Switching this off never silences a change that matters in the next 12 hours.",
  },
  Messages: {
    label: 'Messages',
    secondary:
      'Threads you are in, being added to or taken out of one, and a moderator removing or restoring a message of yours.',
  },
  FamilyObligations: {
    label: 'What your family owes',
    secondary: 'A form, a payment or an RSVP still outstanding.',
  },
  Travel: {
    label: 'Travel',
    secondary: "A trip you are on that is short of seats, and, if you keep the unit's gear, what is still out.",
  },
  SafetyAndAccount: {
    label: 'Safety and account',
    secondary:
      'A youth-protection concern, and a sign-in from a device we do not recognise.',
  },
};

/**
 * How many children get a row before the rest go behind a tap. Two is the common family and eight
 * is where a column of switches stops being scannable.
 */
const CHILDREN_SHOWN = 6;

export function NotificationSettingsScreen({
  me,
  onNavigate,
}: {
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [failed, setFailed] = useState(false);
  const [showAllChildren, setShowAllChildren] = useState(false);

  useEffect(() => {
    let alive = true;

    notificationsApi
      .settings()
      .then((value) => {
        if (alive) setSettings(value);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
    };
  }, []);

  // ToggleRow is optimistic and reverts on a rejection, but it is *controlled* -- so the state it
  // settles onto has to be this one. Updating here after the write resolves is what stops the row
  // snapping back; letting it throw is what makes a failure visible rather than silent.
  async function setCategory(category: string, delivers: boolean) {
    await notificationsApi.setCategory(category, delivers);

    setSettings((current) =>
      current === null
        ? current
        : {
            ...current,
            categories: current.categories.map((c) =>
              c.category === category ? { ...c, delivers } : c,
            ),
          },
    );
  }

  async function setWeeklyDigest(delivers: boolean) {
    await notificationsApi.setWeeklyDigest(delivers);

    setSettings((current) => (current === null ? current : { ...current, weeklyDigest: delivers }));
  }

  async function setChild(personId: string, delivers: boolean) {
    await notificationsApi.setChild(personId, delivers);

    setSettings((current) =>
      current === null
        ? current
        : {
            ...current,
            children: current.children.map((c) =>
              c.personId === personId ? { ...c, delivers } : c,
            ),
          },
    );
  }

  return (
    <Page>
      <AppHeader me={me} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              Your settings
            </p>
            <h1
              className="t-heading"
              style={{
                color: 'var(--color-text-primary)',
                margin: 'var(--space-2) 0 var(--space-2)',
              }}
            >
              Notifications
            </h1>
            <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              These apply across every unit you&rsquo;re in. Nothing here changes what you can see
              &mdash; only what interrupts you.
            </p>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div
          className="flex flex-col"
          style={{ paddingBlock: 'var(--space-6)', gap: 'var(--space-7)' }}
        >
          {failed && (
            <p className="t-body" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
              Your settings couldn&rsquo;t be loaded. Reload the page to try again.
            </p>
          )}

          {settings && (
            <section>
              {settings.categories.map((row) => (
                <ToggleRow
                  key={row.category}
                  label={COPY[row.category].label}
                  secondary={COPY[row.category].secondary}
                  checked={row.delivers}
                  // The server's answer, rendered. Not a check this screen makes -- and it says
                  // who decided rather than greying a switch and leaving the reader guessing.
                  disabledReason={row.canMute ? undefined : (row.whyNot ?? undefined)}
                  onChange={(next) => setCategory(row.category, next)}
                />
              ))}
            </section>
          )}

          {settings && (
            <section>
              <h2
                className="t-subheading"
                style={{ color: 'var(--color-text-primary)', margin: '0 0 var(--space-2)' }}
              >
                The weekly email
              </h2>
              <p
                className="t-body"
                style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}
              >
                One message on Sunday evening with the week ahead, what your family still owes, and
                what&rsquo;s new. It goes out even when there&rsquo;s nothing in it, so a quiet week
                and a broken one don&rsquo;t look the same.
              </p>

              {/* Its own row rather than a seventh category, because it is a channel: the same
                  Sunday message carries announcements, obligations, threads and travel at once. It
                  is also the row somebody arrives here to find, having tapped Unsubscribe in an
                  inbox -- that link deliberately cannot turn it back on, since a forwarded link
                  restoring somebody's mail would be the opt-out undone by a stranger. */}
              <ToggleRow
                label="Weekly summary email"
                secondary="Sunday evening, in your unit's own time zone."
                checked={settings.weeklyDigest}
                onChange={setWeeklyDigest}
              />
            </section>
          )}

          {settings && settings.children.length > 0 && (
            <section>
              <h2
                className="t-subheading"
                style={{ color: 'var(--color-text-primary)', margin: '0 0 var(--space-2)' }}
              >
                Your children&rsquo;s threads
              </h2>
              <p
                className="t-body"
                style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}
              >
                You can always read your children&rsquo;s threads and their unread counts always
                show. This is only about threads you read because your child is in them, like a
                direct thread with a leader &mdash; off by default, because a parent sent every
                message stops reading the ones that matter. A thread you are in yourself counts with
                your own messages above.
              </p>

              {/* Bounded, and not because of dev data. A row per child reads fine for two and is
                  unusable for eight, which is the same trade `ParticipantStrip` and
                  `EventMonthSection` make -- and the dev troop has handed Reese Lindqvist 66
                  children, which is how the unbounded version gets noticed rather than why it is
                  wrong. Bounded rather than truncated: every child stays reachable, because one of
                  them is the one this parent came here to change. */}
              {settings.children.slice(0, showAllChildren ? undefined : CHILDREN_SHOWN).map((child) => (
                <ToggleRow
                  key={child.personId}
                  label={child.displayName}
                  secondary={`Notify me about messages in ${child.displayName}'s threads.`}
                  checked={child.delivers}
                  onChange={(next) => setChild(child.personId, next)}
                />
              ))}

              {!showAllChildren && settings.children.length > CHILDREN_SHOWN && (
                <button
                  type="button"
                  onClick={() => setShowAllChildren(true)}
                  className="t-body text-left"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-link)',
                    cursor: 'pointer',
                    minHeight: 'var(--tap-min)',
                    padding: 'var(--space-3) 0',
                  }}
                >
                  Show {settings.children.length - CHILDREN_SHOWN} more
                </button>
              )}
            </section>
          )}
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}
